import { ScannerButton, ScannerDialog, useScannerConfiguration } from 'src/components/organisms/printing/Scanner';
import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionPopup } from 'src/components/atoms/WarehouseSelector';
import { Children, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, MenuItem, TextField, Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-premium';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import ContentCardsContainer, { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { useReceiveProducts } from 'src/hooks/api/requestConfigs/warehouse';
import { useUpdateProduct } from 'src/hooks/api/requestConfigs/products';
import { useSettingsContext } from 'src/hooks/useSettings';
import {
    useReceivingOrdersTable,
    useReceivingOrderMatchesTable,
} from 'src/hooks/table/configs/useReceivingOrdersTable';
import type { ReceivingOrderRow as BaseReceivingOrderRow, ReceivingOrdersTableData } from 'src/hooks/table/configs/useReceivingOrdersTable';
import ClearIcon from '@mui/icons-material/Clear';
import PrintIcon from '@mui/icons-material/Print';
import { ProductLabelPrintJob, useDispatchPrintingSettings } from 'src/components/organisms/printing/PrintingManager';
import type { DispatchPrintingSettings, ProductLabelPrintResult } from 'src/components/organisms/printing/PrintingManager';

type Identifier = string | number;
type ReceivingOrderRow = BaseReceivingOrderRow & { EANCode?: Identifier | null; ManufacturerAltProductId?: Identifier | null };
type ProductLabelRequest = { productId: Identifier; copies: number; settings: DispatchPrintingSettings };
type ProductLabelJob = ProductLabelRequest & { key: number };
type ProductBarcodeRequest = { productId: number; barcode: string };
type ProductBarcodeJob = ProductBarcodeRequest & { key: number };
type PrintNotice = { key: number; severity: 'success' | 'warning' | 'error'; message: string };

const matchesProductIdentifier = (row: ReceivingOrderRow, scan: string) => [row.ProductId, row.ManufacturerProductId, row.EANCode].some((value) => value != null && String(value).trim() !== '' && String(value).trim() === scan);
const withEanColumn = (columns: GridColDef[]): GridColDef[] => columns.some((column) => column.field === 'EANCode') ? columns.map((column) => column.field === 'EANCode' ? { ...column, headerName: 'Barcode(EAN)', type: 'string' } : column) : [...columns, { field: 'EANCode', headerName: 'Barcode(EAN)', type: 'string', minWidth: 150 }];

const isValidEan = (value: string) => {
    if (!/^(?:\d{8}|\d{13})$/.test(value)) return false;
    const digits = value.slice(0, -1);
    const sum = Array.from(digits).reduce((total, digit, index) => total + Number(digit) * ((digits.length - index) % 2 === 1 ? 3 : 1), 0);
    return (10 - sum % 10) % 10 === Number(value[value.length - 1]);
};

// Mount only for a confirmed receipt. A fresh keyed instance also supports
// successive updates to the same product with the hook's id-only dependency.
const ProductBarcodeUpdate = ({ job, onComplete }: { job: ProductBarcodeJob; onComplete: (failed: boolean) => void }) => {
    // The update endpoint accepts this partial body; its shared create type is broader.
    const body = useMemo(() => ({ ManufacturerAltProductId: job.barcode } as Parameters<typeof useUpdateProduct>[1]), [job.barcode]);
    const [, , , loading, status] = useUpdateProduct(job.productId, body);
    const completedRef = useRef(false);
    useEffect(() => {
        if (completedRef.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || (typeof status === 'number' && status >= 400);
        const succeeded = status === 'success' || (typeof status === 'number' && status >= 200 && status < 300);
        if (!failed && !succeeded) return;
        completedRef.current = true;
        onComplete(failed);
    }, [loading, status, onComplete]);
    return null;
};

const receivingAdviceTypes: Record<string, string> = { '1': 'Purchase Order', '2': 'Transfer Order', '3': 'Return To Warehouse Order' };
const receivingConditions = { 1: 'Broken', 2: 'OK' } as const;
type ReceivingCondition = keyof typeof receivingConditions;
type ReceivePayload = NonNullable<Parameters<typeof useReceiveProducts>[0]>;

type ReceiveValues = {
    Condition: ReceivingCondition;
};

const receiveSchema = Yup.object({
    Condition: Yup.number().oneOf([1, 2], 'Select a receiving condition').required('Condition is required'),
});

type ReceivingStatusValue = { kind: 'remaining' | 'surplus' | 'done' | 'receiving' | 'pending'; quantity?: number };

const getReceivingStatus = (expectedValue: unknown, receivedValue: unknown, redundantValue: unknown = 0): ReceivingStatusValue => {
    const expected = Number(expectedValue);
    const received = Number(receivedValue);
    const redundant = Number(redundantValue);

    if (Number.isFinite(redundant) && redundant > 0) return { kind: 'surplus', quantity: redundant };
    if (!Number.isFinite(expected) || !Number.isFinite(received)) return { kind: 'pending' };
    if (received > expected) return { kind: 'surplus', quantity: received - expected };
    if (received === expected) return { kind: 'done' };
    return { kind: 'remaining', quantity: expected - received };
};

type ReceivingFormVisibilityProps = {
    isReceivingFormVisible: boolean;
    onReceivingFormVisibilityChange: (visible: boolean) => void;
};

type LayoutProps = ReceivingFormVisibilityProps & {
    warehouseId: Identifier | null;
    onWarehouseChange: (value: Identifier) => void;
    scanner?: ReactNode;
    receiveAction?: ReactNode;
    dialog?: ReactNode;
    printingFeedback?: ReactNode;
    children: ReactNode;
};

const ReceivingOrdersLayout = ({ warehouseId, onWarehouseChange, scanner, receiveAction, dialog, printingFeedback, children, isReceivingFormVisible, onReceivingFormVisibilityChange }: LayoutProps) => {
    const scannerConfiguration = useScannerConfiguration();
    return (
    <>
        <PageHeader {...{
            title: 'Receiving Orders',
            subTitle: 'Receive Products from Suppliers etc, and add them to your Warehouse Inventory',
            components: [
                { isInGroupedMenu: true, component: <ScannerButton variant="outlined" onClick={scannerConfiguration.openDialog} /> },
                ...(scanner ? [{ position: 'center', component: scanner }] : []),
                { component: <WarehouseSelector value={warehouseId} onChange={onWarehouseChange} /> },

                { component: receiveAction ?? <Button variant="contained" disabled>Receive</Button> },
                {
                    isInGroupedMenu: true,
                    component: <CustomButton
                        variant="text"
                        title={isReceivingFormVisible ? 'Hide receiving form' : 'Show receiving form'}
                        onClick={() => onReceivingFormVisibilityChange(!isReceivingFormVisible)}
                    />,
                },
            ],
        }} />
        <ScannerDialog {...scannerConfiguration.dialogProps} />
        {printingFeedback}
        {/* This container clones every child, so omit hidden cards entirely. */}
        <ContentCardsContainer>{Children.toArray(children)}</ContentCardsContainer>
        {dialog}
        <Footer />
    </>
);
};

export const ReceivingOrders = () => {
    const { settings, setSetting } = useSettingsContext();
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    // Use the existing persisted settings store; default to visible for new users.
    const isReceivingFormVisible = settings?.receivingOrdersFormVisible ?? true;
    const onReceivingFormVisibilityChange = useCallback((visible: boolean) => {
        setSetting('receivingOrdersFormVisible', visible);
    }, [setSetting]);
    const onWarehouseChange = (value: Identifier) => selectWarehouse(value);

    // Keep the print queue above the warehouse-keyed station so changing the
    // warehouse or clearing the form cannot drop an already accepted print job.
    const [labelJobs, setLabelJobs] = useState<ProductLabelJob[]>([]);
    const [printNotices, setPrintNotices] = useState<PrintNotice[]>([]);
    const labelSequence = useRef(0);
    const enqueueProductLabel = useCallback((request: ProductLabelRequest) => {
        const job = { ...request, key: ++labelSequence.current };
        setLabelJobs((current) => [...current, job]);
    }, []);
    const labelJob = labelJobs[0];
    const [barcodeJobs, setBarcodeJobs] = useState<ProductBarcodeJob[]>([]);
    const [barcodeUpdateFailed, setBarcodeUpdateFailed] = useState(false);
    const [barcodeAttempt, setBarcodeAttempt] = useState(0);
    const barcodeSequence = useRef(0);
    const enqueueProductBarcode = useCallback((request: ProductBarcodeRequest) => {
        const job = { ...request, key: ++barcodeSequence.current };
        setBarcodeJobs((current) => [...current, job]);
    }, []);
    const barcodeJob = barcodeJobs[0];
    const completeProductBarcode = useCallback((failed: boolean) => {
        if (failed) { setBarcodeUpdateFailed(true); return; }
        setBarcodeJobs((current) => current.slice(1));
    }, []);
    const completeProductLabel = useCallback((result: ProductLabelPrintResult) => {
        if (!labelJob) return;
        const prefix = `Product ${labelJob.productId} (${labelJob.copies} labels): `;
        const outcome = result.outcome;
        const severity = result.error ? 'error' : outcome?.method === 'printer' ? 'success' : 'warning';
        const message = result.error ? `${prefix}${result.error} Use Product Label to retry printing.` : outcome?.method === 'printer' ? `${prefix}Sent to "${outcome.printer}".` : `${prefix}${outcome?.method === 'savePdf' ? `PDF downloaded. Print ${labelJob.copies} copies from the downloaded label.` : 'Printing skipped.'}${outcome?.reason ? ` ${outcome.reason}` : ''}`;
        setPrintNotices((current) => [...current, { key: labelJob.key, severity, message }]);
        setLabelJobs((current) => current.filter((job) => job.key !== labelJob.key));
    }, [labelJob]);
    const printingFeedback = <>
        {barcodeJob && (barcodeUpdateFailed ? <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => { setBarcodeAttempt((current) => current + 1); setBarcodeUpdateFailed(false); }}>Retry barcode</Button>}>Products were received, but barcode {barcodeJob.barcode} could not be saved for product {barcodeJob.productId}. Retry saves only the barcode. Further barcode saves are queued.</Alert> : <Alert severity="info" sx={{ mb: 2 }}>Saving barcode for product {barcodeJob.productId}…</Alert>)}
        {/* {labelJobs.length > 0 && <Alert severity="info" sx={{ mb: 2 }}>Printing product labels… ({labelJobs.length} queued jobs)</Alert>} */}
        {/* {printNotices.map((notice) => <Alert key={notice.key} severity={notice.severity} sx={{ mb: 2 }} onClose={() => setPrintNotices((current) => current.filter((item) => item.key !== notice.key))}>{notice.message}</Alert>)} */}
    </>;

    // Mount the data hook only after a warehouse is selected. A warehouse change
    // also discards the previous scan, duplicate choices, and quantity immediately.
    return <>
        <WarehouseSelectionPopup />
        {barcodeJob && !barcodeUpdateFailed && <ProductBarcodeUpdate key={`product-barcode-${barcodeJob.key}-${barcodeAttempt}`} job={barcodeJob} onComplete={completeProductBarcode} />}
        {labelJob && <ProductLabelPrintJob key={`product-label-${labelJob.key}`} productId={labelJob.productId} copies={labelJob.copies} settings={labelJob.settings} onComplete={completeProductLabel} />}
        {warehouseId == null || warehouseId === '' ? (
            <ReceivingOrdersLayout
                warehouseId={null}
                onWarehouseChange={onWarehouseChange}
                isReceivingFormVisible={isReceivingFormVisible}
                onReceivingFormVisibilityChange={onReceivingFormVisibilityChange}
                printingFeedback={printingFeedback}
            >
                <ContentCard {...{ cardId: 'ReceiveProducts', md: 12, cardTypes: [CardType.noHeader] }}>
                    <Typography>Select a warehouse to scan and receive products.</Typography>
                </ContentCard>
            </ReceivingOrdersLayout>
        ) : <ReceivingStation key={String(warehouseId)} warehouseId={warehouseId} onWarehouseChange={onWarehouseChange} isReceivingFormVisible={isReceivingFormVisible} onReceivingFormVisibilityChange={onReceivingFormVisibilityChange} printingFeedback={printingFeedback} onPrintProductLabel={enqueueProductLabel} onSaveProductBarcode={enqueueProductBarcode} />}
    </>;
};

const ReceivingStation = ({ warehouseId, onWarehouseChange, isReceivingFormVisible, onReceivingFormVisibilityChange, printingFeedback, onPrintProductLabel, onSaveProductBarcode }: ReceivingFormVisibilityProps & {
    warehouseId: Identifier;
    onWarehouseChange: (value: Identifier) => void;
    printingFeedback: ReactNode;
    onPrintProductLabel: (request: ProductLabelRequest) => void;
    onSaveProductBarcode: (request: ProductBarcodeRequest) => void;
}) => {
    const { settings, setSetting } = useSettingsContext();
    const printingSettings = useDispatchPrintingSettings();
    const printProductLabel = settings?.receivingOrdersPrintProductLabel === true || settings?.receivingOrdersPrintProductLabel === 'true';
    const printProductLabelRef = useRef(printProductLabel);
    printProductLabelRef.current = printProductLabel;
    const pendingProductLabelRef = useRef<ProductLabelRequest | null>(null);
    const [receiveError, setReceiveError] = useState('');
    const [barcodeValue, setBarcodeValue] = useState('');
    const barcodeValueRef = useRef('');
    const pendingProductBarcodeRef = useRef<ProductBarcodeRequest | null>(null);
    const updateBarcodeValue = useCallback((value: string) => {
        barcodeValueRef.current = value;
        setBarcodeValue(value);
    }, []);
    const [labelDialog, setLabelDialog] = useState<{ productId: Identifier; copies: string } | null>(null);
    const reprintSubmittedRef = useRef(false);
    const selectRowRef = useRef<((row: ReceivingOrderRow) => void) | null>(null);
    const handleTableSelect = useCallback((row: ReceivingOrderRow) => selectRowRef.current?.(row), []);
    const [refreshKey, setRefreshKey] = useState(0);
    const [tableColumns, tableSettings, { rows, status }] = useReceivingOrdersTable(warehouseId, refreshKey, handleTableSelect);
    const columns = useMemo(() => withEanColumn(tableColumns), [tableColumns]);
    const [receivePayload, setReceivePayload] = useState<ReceivePayload | null>(null);
    const receiveResult = useReceiveProducts(receivePayload);
    const receiveRowAtSubmitRef = useRef<unknown>(null);
    const receiveStatusAtSubmitRef = useRef(receiveResult?.status);
    const receiveObservedLoadingRef = useRef(false);
    const [scannerValue, setScannerValue] = useState('');
    const scannerValueRef = useRef('');
    const [selectedRowId, setSelectedRowId] = useState<Identifier | null>(null);
    const [selectedRowSnapshot, setSelectedRowSnapshot] = useState<ReceivingOrderRow | null>(null);
    const selectedRowRef = useRef<ReceivingOrderRow | null>(null);
    const [duplicateScan, setDuplicateScan] = useState<string | null>(null);
    const scannerRef = useRef<HTMLInputElement>(null);
    const [quantityValue, setQuantityValue] = useState<number | ''>('');
    const quantityValueRef = useRef('');
    const [isQuantityScanMode, setIsQuantityScanMode] = useState(false);
    const isQuantityScanModeRef = useRef(false);
    const receivingConditionRef = useRef<ReceivingCondition>(2);
    const receiveRequestPendingRef = useRef(false);
    const hasRemainingQuantityRef = useRef(false);
    const receiveRowSignatureAtSubmitRef = useRef('');
    const submitReceiveRef = useRef<(() => boolean) | null>(null);

    const updateQuantityValue = useCallback((value: number | '') => {
        quantityValueRef.current = value === '' ? '' : String(value);
        setQuantityValue(value);
    }, []);

    const updateQuantityScanMode = useCallback((active: boolean) => {
        isQuantityScanModeRef.current = active;
        setIsQuantityScanMode(active);
    }, []);

    // Use the same API rows as the main table, independent of grid filtering.
    // Ignore rows retained by the request hook from another warehouse.
    const warehouseRows = useMemo(() => rows.filter((row) =>
        row.WarehouseId != null && String(row.WarehouseId) === String(warehouseId)
    ), [rows, warehouseId]);

    const findMatches = useCallback((scan: string) => warehouseRows.filter((row) => matchesProductIdentifier(row, scan)), [warehouseRows]);

    const selectedRow: ReceivingOrderRow | null = warehouseRows.find((row) =>
        selectedRowId != null && String(row.ReceivingAdviceRowId) === String(selectedRowId)
    ) ?? selectedRowSnapshot;
    selectedRowRef.current = selectedRow;
    const duplicateRows = useMemo(() => duplicateScan == null ? [] : findMatches(duplicateScan), [duplicateScan, findMatches]);

    const receiveRow = receiveResult?.rows?.[0] ?? null;
    const receiveRowSignature = receiveRow ? [receiveRow.DateReceived, receiveRow.ReceivingAdviceId, receiveRow.ProductId, receiveRow.QtyReceived, receiveRow.QtyRedundant, receiveRow.ReceivingConditionId].map((value) => String(value ?? '')).join('|') : '';
    const [lastReceiveRow, setLastReceiveRow] = useState<typeof receiveRow>(null);
    const receivedQuantity = lastReceiveRow?.QtyReceived ?? selectedRow?.ReceivedQty ?? selectedRow?.QuantityReceive ?? 0;
    const receivingStatus = getReceivingStatus(selectedRow?.QuantityExpected, receivedQuantity, lastReceiveRow?.QtyRedundant);
    const hasRemainingQuantity = selectedRow != null && receivingStatus.kind === 'remaining';
    hasRemainingQuantityRef.current = hasRemainingQuantity;

    const formik = useFormik<ReceiveValues>({
        initialValues: { Condition: 2 },
        validationSchema: receiveSchema,
        onSubmit: (values, helpers) => {
            receivingConditionRef.current = values.Condition;
            if (!submitReceiveRef.current?.()) helpers.setSubmitting(false);
        },
    });
    const { resetForm, setSubmitting, setFieldValue } = formik;

    const submitReceive = useCallback(() => {
        const row = selectedRowRef.current;
        const latestQuantity = Number(quantityValueRef.current);
        const condition = receivingConditionRef.current;

        if (receiveRequestPendingRef.current || !isQuantityScanModeRef.current || !hasRemainingQuantityRef.current || !row || row.ProductId == null || row.ProductId === '' || row.StorageItemId == null || row.StorageItemId === '') {
            return false;
        }
        if (!Number.isFinite(latestQuantity) || latestQuantity <= 0 || ![1, 2].includes(condition)) {
            return false;
        }
        if (printProductLabelRef.current && !Number.isSafeInteger(latestQuantity)) {
            setReceiveError('Enter a whole number of products when printing one label per product.');
            return false;
        }

        const barcode = barcodeValueRef.current.trim();
        const productId = Number(row.ProductId);
        if (barcode && (!Number.isSafeInteger(productId) || productId <= 0)) {
            setReceiveError('A numeric product ID is required to save the barcode.');
            return false;
        }
        pendingProductBarcodeRef.current = barcode ? { productId, barcode } : null;

        // All scanner-sensitive values come from refs here. This path does not wait for
        // React state, a re-render, blur, or Formik validation before building the request.
        receiveRequestPendingRef.current = true;
        setReceiveError('');
        pendingProductLabelRef.current = printProductLabelRef.current ? { productId: row.ProductId, copies: latestQuantity, settings: printingSettings } : null;
        receiveRowAtSubmitRef.current = receiveRow;
        receiveStatusAtSubmitRef.current = receiveResult?.status;
        receiveObservedLoadingRef.current = false;
        setSubmitting(true);
        receiveRowSignatureAtSubmitRef.current = receiveRowSignature;
        setReceivePayload({ receivingAdviceId: row.ReceivingAdviceId, productId: row.ProductId, storageItemId: row.StorageItemId, condition, warehouseId, quantityReceive: latestQuantity });
        return true;
    }, [printingSettings, receiveRow, receiveResult?.status, receiveRowSignature, setSubmitting, warehouseId]);
    submitReceiveRef.current = submitReceive;

    useEffect(() => {
        if (!receiveRequestPendingRef.current) return;
        if (receiveResult?.loading) { receiveObservedLoadingRef.current = true; return; }
        const requestStatus = receiveResult?.status;
        const failed = requestStatus === 'error' || requestStatus === 'aborted' || (typeof requestStatus === 'number' && requestStatus >= 400);
        const freshStatus = receiveObservedLoadingRef.current || requestStatus !== receiveStatusAtSubmitRef.current;
        const succeeded = requestStatus === 'success' || (typeof requestStatus === 'number' && requestStatus >= 200 && requestStatus < 300);
        if (failed || (succeeded && !receiveRow)) {
            if (!freshStatus) return;
            pendingProductLabelRef.current = null;
            pendingProductBarcodeRef.current = null;
            receiveRequestPendingRef.current = false;
            setReceivePayload(null);
            setSubmitting(false);
            setReceiveError('The receipt could not be confirmed. No labels were printed. Check the receiving order before trying again.');
            return;
        }
        if (!receiveRow || (receiveRow === receiveRowAtSubmitRef.current && receiveRowSignature === receiveRowSignatureAtSubmitRef.current && !receiveObservedLoadingRef.current)) return;
        // Ignore retained data for a different receipt while the new request starts.
        if (String(receiveRow.ProductId) !== String(receivePayload?.productId) || String(receiveRow.ReceivingAdviceId) !== String(receivePayload?.receivingAdviceId)) return;

        const productBarcode = pendingProductBarcodeRef.current;
        pendingProductBarcodeRef.current = null;
        if (productBarcode) onSaveProductBarcode(productBarcode);

        const productLabel = pendingProductLabelRef.current;
        pendingProductLabelRef.current = null;
        // The submitted quantity is this receipt's quantity, not the cumulative
        // QtyReceived total displayed by the receiving API.
        if (productLabel) onPrintProductLabel(productLabel);

        const quantityReceived = Number(receiveRow.QtyReceived);
        const receivingConditionId = Number(receiveRow.ReceivingConditionId);
        if (Number.isFinite(quantityReceived)) updateQuantityValue(quantityReceived);
        if (receivingConditionId === 1 || receivingConditionId === 2) {
            receivingConditionRef.current = receivingConditionId;
            setFieldValue('Condition', receivingConditionId, false);
        }
        setLastReceiveRow(receiveRow);
        hasRemainingQuantityRef.current = getReceivingStatus(selectedRowRef.current?.QuantityExpected, receiveRow.QtyReceived, receiveRow.QtyRedundant).kind === 'remaining';
        // Reset the request so another receipt can use the same entered quantity.
        setReceivePayload(null);
        scannerValueRef.current = '';
        setScannerValue('');
        updateQuantityScanMode(false);
        receiveRequestPendingRef.current = false;
        setSubmitting(false);
        receiveRowSignatureAtSubmitRef.current = receiveRowSignature;
        setRefreshKey((current) => current + 1);
    }, [receivePayload, receiveResult?.loading, receiveResult?.status, receiveRow, receiveRowSignature, onPrintProductLabel, onSaveProductBarcode, setFieldValue, setSubmitting, updateQuantityScanMode, updateQuantityValue]);

    const quantityToReceive = Number(quantityValue);
    const hasReceivingIdentifiers = selectedRow != null && selectedRow.ProductId != null && selectedRow.ProductId !== '' && selectedRow.StorageItemId != null && selectedRow.StorageItemId !== '';
    const canEditQuantity = hasRemainingQuantity && !formik.isSubmitting;
    const canReceive = isQuantityScanMode && hasRemainingQuantity && hasReceivingIdentifiers && Number.isFinite(quantityToReceive) && quantityToReceive > 0 && (!printProductLabel || Number.isSafeInteger(quantityToReceive)) && [1, 2].includes(formik.values.Condition) && !formik.isSubmitting;
    const displayedStatus: ReceivingStatusValue = formik.isSubmitting ? { kind: 'receiving' } : receivingStatus;

    const clearReceivingForm = useCallback(() => {
        if (receiveRequestPendingRef.current) return;
        pendingProductLabelRef.current = null;
        pendingProductBarcodeRef.current = null;
        updateBarcodeValue('');
        setReceiveError('');
        const filterModel = tableSettings.filterModel ?? { items: [] };
        const items = filterModel.items.filter((item) => item.field !== 'ReceivingAdviceId');
        if (tableSettings.initialFilter?.field === 'ReceivingAdviceId') tableSettings.initialFilter = undefined;
        if (items.length !== filterModel.items.length) tableSettings.apiRef.current.setFilterModel({ ...filterModel, items });

        receivingConditionRef.current = 2;
        resetForm();
        setReceivePayload(null);
        scannerValueRef.current = '';
        setScannerValue('');
        selectedRowRef.current = null;
        hasRemainingQuantityRef.current = false;
        setSelectedRowId(null);
        setSelectedRowSnapshot(null);
        setDuplicateScan(null);
        updateQuantityValue('');
        updateQuantityScanMode(false);
        setLastReceiveRow(null);
        receiveRequestPendingRef.current = false;
        receiveRowSignatureAtSubmitRef.current = receiveRowSignature;
    }, [receiveRowSignature, resetForm, tableSettings, updateBarcodeValue, updateQuantityScanMode, updateQuantityValue]);

    const selectRow = useCallback((row: ReceivingOrderRow) => {
        if (receiveRequestPendingRef.current) return;
        setReceiveError('');
        receivingConditionRef.current = 2;
        resetForm();
        selectedRowRef.current = row;
        const existingBarcode = row.EANCode ?? row.ManufacturerAltProductId;
        updateBarcodeValue(existingBarcode == null ? '' : String(existingBarcode));
        updateQuantityValue(1);
        setLastReceiveRow(null);
        setSelectedRowId(row.ReceivingAdviceRowId);
        setSelectedRowSnapshot(row);
        setDuplicateScan(null);
        const hasRemaining = getReceivingStatus(row.QuantityExpected, row.ReceivedQty ?? row.QuantityReceive ?? 0).kind === 'remaining';
        hasRemainingQuantityRef.current = hasRemaining;
        updateQuantityScanMode(hasRemaining);
    }, [resetForm, updateBarcodeValue, updateQuantityScanMode, updateQuantityValue]);
    selectRowRef.current = selectRow;

    useEffect(() => {
        if (!formik.isSubmitting && duplicateScan == null && labelDialog == null && !['Quantity', 'Barcode'].includes(document.activeElement?.getAttribute('name') ?? '')) scannerRef.current?.focus();
    }, [duplicateScan, formik.isSubmitting, isQuantityScanMode, selectedRowId, labelDialog != null]);

    const handleScannerSubmit = (submittedScannerValue = scannerValueRef.current) => {
        if (receiveRequestPendingRef.current || labelDialog != null || duplicateScan != null) return;

        const scan = submittedScannerValue.trim();
        if (!scan) return;
        scannerValueRef.current = '';
        setScannerValue('');

        // Scanning reveals the form and persists the same preference as the menu.
        if (!isReceivingFormVisible) onReceivingFormVisibilityChange(true);

        // Resolve all three identifiers before commands, quantities, or assigning a
        // new EAN. String comparison preserves leading zeroes and keeps every row.
        const activeRow = selectedRowRef.current;
        const matches = findMatches(scan);
        if (matches.length === 1 && activeRow && String(matches[0].ReceivingAdviceRowId) === String(activeRow.ReceivingAdviceRowId) && isQuantityScanModeRef.current) {
            const currentQuantity = Number(quantityValueRef.current);
            const nextQuantity = Number.isFinite(currentQuantity) ? currentQuantity + 1 : 1;
            updateQuantityValue(nextQuantity);
            return;
        }

        if (matches.length === 0) {
            // Only an unmatched valid EAN can be assigned to the active product.
            if (activeRow && isValidEan(scan)) {
                setReceiveError('');
                updateBarcodeValue(scan);
                return;
            }

            const scannerCommand = scan.toLowerCase();
            if (scannerCommand.includes('cancel')) {
                clearReceivingForm();
                return;
            }
            if (scannerCommand.includes('ok')) {
                submitReceive();
                return;
            }

            if (isQuantityScanModeRef.current) {
                const quantity = Number(scan);
                if (Number.isFinite(quantity) && quantity >= 0 && quantity <= 1000) {
                    updateQuantityValue(quantity);
                    scannerRef.current?.focus();
                    return;
                }

                // Numeric scans above 1000 continue to the unmatched-identifier error.
                if (!Number.isFinite(quantity) || quantity <= 1000) {
                    scannerRef.current?.focus();
                    return;
                }
            }

            // An unrecognized scan never discards the loaded form.
            const invalidEan = activeRow && /^(?:\d{8}|\d{13})$/.test(scan) && !isValidEan(scan);
            setReceiveError(invalidEan ? `Scanned value "${scan}" has an invalid EAN check digit. The loaded product has been kept.` : `Scanned value "${scan}" did not match a Product ID, Manufacturer Product ID or EAN${activeRow ? ', or a valid new EAN-8/EAN-13 barcode' : ''}. The form has been kept.`);
            scannerRef.current?.focus();
            return;
        }

        // Clear the previous display only after a product match is found.
        setReceiveError('');
        updateBarcodeValue('');
        receivingConditionRef.current = 2;
        resetForm();
        setReceivePayload(null);
        updateQuantityValue('');
        updateQuantityScanMode(false);
        setLastReceiveRow(null);
        selectedRowRef.current = null;
        setSelectedRowId(null);
        setSelectedRowSnapshot(null);
        setDuplicateScan(null);

        if (matches.length === 1) {
            selectRow(matches[0]);
        } else {
            setDuplicateScan(scan);
        }
    };

    const scanner = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
                inputRef={scannerRef}
                disabled={formik.isSubmitting}
                value={scannerValue}
                onChange={(event) => {
                    scannerValueRef.current = event.target.value;
                    setScannerValue(event.target.value);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        const submittedScannerValue = (event.target as HTMLInputElement).value;
                        scannerValueRef.current = submittedScannerValue;
                        handleScannerSubmit(submittedScannerValue);
                    }
                }}
                placeholder={isQuantityScanMode ? 'Scan quantity, Product ID, Manufacturer ID, EAN, OK or Cancel' : 'Scan Product ID, Manufacturer Product ID or EAN'}
                inputProps={{ 'aria-label': isQuantityScanMode ? 'Scan quantity, product ID, manufacturer product ID, EAN, OK or Cancel' : 'Scan product ID, manufacturer product ID or EAN' }}
                autoFocus
                sx={{ width: { xs: 180, sm: 300, md: 380 } }}
            />
            <CustomButton disabled={formik.isSubmitting} title="Submit" size="small" iconButton={true} variant="outlined" icon={<ArrowForwardIcon />} onClick={() => handleScannerSubmit()} />
        </Box>
    );

    const openProductLabelDialog = () => {
        const productId = selectedRow?.ProductId;
        if (productId == null || productId === '' || receiveRequestPendingRef.current) return;
        reprintSubmittedRef.current = false;
        setLabelDialog({ productId, copies: String(receivedQuantity ?? 0) });
    };
    const labelCopies = Number(labelDialog?.copies);
    const validLabelCopies = labelDialog?.copies.trim() !== '' && Number.isSafeInteger(labelCopies) && labelCopies > 0;
    const submitProductLabel = () => {
        if (!labelDialog || !validLabelCopies || reprintSubmittedRef.current) return;
        reprintSubmittedRef.current = true;
        onPrintProductLabel({ productId: labelDialog.productId, copies: labelCopies, settings: printingSettings });
        setLabelDialog(null);
    };
    const productCardStatus = selectedRow ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <ReceivingStatus status={displayedStatus} hasSelectedRow />
        <Button type="button" size="small" variant="outlined" startIcon={<PrintIcon />} disabled={formik.isSubmitting || selectedRow.ProductId == null || selectedRow.ProductId === ''} onClick={openProductLabelDialog}>Product Label</Button>
    </Box> : null;
    const receivingOrderId = lastReceiveRow?.ReceivingAdviceId ?? selectedRow?.ReceivingAdviceId;
    const hasReceivingOrderId = receivingOrderId != null && receivingOrderId !== '';
    const currentFilterModel = tableSettings.filterModel ?? { items: [] };
    const isReceivingOrderFilter = (item: (typeof currentFilterModel.items)[number]) => item.field === 'ReceivingAdviceId' && item.operator === 'equals' && String(item.value) === String(receivingOrderId);
    const isReceivingOrderFiltered = hasReceivingOrderId && currentFilterModel.items.some(isReceivingOrderFilter);
    const viewReceivingOrder = () => {
        if (!hasReceivingOrderId) return;
        window.open(`/warehouse/receiving-orders/edit?id=${encodeURIComponent(String(receivingOrderId))}`, '_blank', 'noopener,noreferrer');
    };
    const filterReceivingOrder = () => {
        if (!hasReceivingOrderId) return;
        if (isReceivingOrderFiltered) {
            tableSettings.initialFilter = undefined;
            tableSettings.apiRef.current.setFilterModel({ ...currentFilterModel, items: currentFilterModel.items.filter((item) => !isReceivingOrderFilter(item)) });
            return;
        }
        tableSettings.initialFilter = { field: 'ReceivingAdviceId', operator: 'equals', value: String(receivingOrderId) };
        // Apply to the mounted grid too, since its filterModel is controlled.
        tableSettings.apiRef.current.setFilterModel({ items: [tableSettings.initialFilter], quickFilterValues: [] });
    };

    return (
        <ReceivingOrdersLayout
            warehouseId={warehouseId}
            onWarehouseChange={(value) => { if (!receiveRequestPendingRef.current) onWarehouseChange(value); }}
            isReceivingFormVisible={isReceivingFormVisible}
            onReceivingFormVisibilityChange={onReceivingFormVisibilityChange}
            scanner={scanner}
            printingFeedback={<>{printingFeedback}{receiveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReceiveError('')}>{receiveError}</Alert>}</>}
            receiveAction={<CustomButton title="Receive" icon={<ArrowUpwardIcon />} isSubmitting={formik.isSubmitting} disabled={!canReceive} onClick={submitReceive} />}
            dialog={<>{duplicateScan != null && (
                <ReceivingRowPicker
                    scan={duplicateScan}
                    rows={duplicateRows}
                    status={status}
                    onSelect={selectRow}
                    onClose={() => {
                        setDuplicateScan(null);
                        scannerRef.current?.focus();
                    }}
                />
            )}
                {labelDialog && <Dialog open onClose={() => setLabelDialog(null)} fullWidth maxWidth="xs" aria-labelledby="product-label-dialog-title">
                    <Box component="form" onSubmit={(event) => { event.preventDefault(); submitProductLabel(); }} noValidate>
                        <DialogTitle id="product-label-dialog-title">Print product label</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ mb: 2 }}>Product {labelDialog.productId}</Typography>
                            <TextField autoFocus fullWidth label="Number of labels" type="number" value={labelDialog.copies} inputProps={{ min: 1, step: 1 }} error={!validLabelCopies} helperText={!validLabelCopies ? 'Enter a positive whole number.' : 'One label per product.'} onChange={(event) => setLabelDialog({ ...labelDialog, copies: event.target.value })} />
                        </DialogContent>
                        <DialogActions><Button type="button" onClick={() => setLabelDialog(null)}>Cancel</Button><Button type="submit" variant="contained" startIcon={<PrintIcon />} disabled={!validLabelCopies}>Print</Button></DialogActions>
                    </Box>
                </Dialog>}
            </>}
        >
            {isReceivingFormVisible && (
                <ContentCard key="receiving-order" {...{ cardId: 'Receiving Order', md: 6, components: [
                    { isInGroupedMenu: false, component: <CustomButton title="View order" variant="outlined" icon={<VisibilityIcon />} disabled={!hasReceivingOrderId} onClick={viewReceivingOrder} /> },
                    { isInGroupedMenu: false, component: <CustomButton title={isReceivingOrderFiltered ? "Remove order filter" : "Filter order"} variant={isReceivingOrderFiltered ? "contained" : "outlined"} icon={<FilterAltIcon />} disabled={!hasReceivingOrderId} onClick={filterReceivingOrder} /> },
                    { isInGroupedMenu: false, component: <CustomButton title="Clear" variant="outlined" icon={<ClearIcon />} disabled={formik.isSubmitting} onClick={clearReceivingForm} /> },
                ] }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2, width: '100%' }}>
                        <TextField fullWidth  label="Order ID" value={receivingOrderId ?? ''} InputProps={{ readOnly: true }} />
                        <TextField fullWidth  label="Order Row ID" value={selectedRow?.ReceivingAdviceRowId ?? ''} InputProps={{ readOnly: true }} />
                        <TextField fullWidth label="Receiving order type" value={receivingAdviceTypes[String(selectedRow?.ReceivingAdviceTypeId)] ?? String(selectedRow?.ReceivingAdviceTypeId ?? '')} InputProps={{ readOnly: true }} sx={{ gridColumn: { sm: '1 / -1' } }} />
                        <TextField fullWidth  label="Sender" value={selectedRow?.Sender ?? ''} InputProps={{ readOnly: true }} sx={{ gridColumn: { sm: '1 / -1' } }} />
                    </Box>
                </ContentCard>
            )}

            {isReceivingFormVisible && (
                <ContentCard key="product" {...{ cardId: 'Product', md: 6, components: productCardStatus ? [{ isInGroupedMenu: false, component: productCardStatus }] : [] }}>
                    <Box component="form" onSubmit={(event) => { event.preventDefault(); submitReceive(); }} noValidate sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, width: '100%' }}>
                        <TextField fullWidth label="Product name" value={lastReceiveRow?.ProductName ?? selectedRow?.ProductName ?? ''} InputProps={{ readOnly: true }} sx={{ gridColumn: { sm: '1 / -1' } }} />
                        <TextField fullWidth size="small" label="Product ID" value={lastReceiveRow?.ProductId ?? selectedRow?.ProductId ?? ''} InputProps={{ readOnly: true }} />
                        <TextField fullWidth size="small" label="Manufacturer Product ID" value={selectedRow?.ManufacturerProductId ?? ''} InputProps={{ readOnly: true }} />
                        <TextField fullWidth size="small" label="Expected" value={selectedRow?.QuantityExpected ?? ''} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }} />
                        <TextField fullWidth size="small" label="Received" value={receivedQuantity ?? ''} InputProps={{ readOnly: true }} inputProps={{ style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }} />
                        <FormControlLabel label="Print product label" control={<Checkbox checked={printProductLabel} disabled={formik.isSubmitting} onChange={(_, checked) => { printProductLabelRef.current = checked; setSetting('receivingOrdersPrintProductLabel', checked); }} />} />
                        <TextField fullWidth label="Barcode(EAN)" name="Barcode" type="text" value={barcodeValue} disabled={!selectedRow || formik.isSubmitting} onChange={(event) => updateBarcodeValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); updateBarcodeValue((event.target as HTMLInputElement).value); scannerRef.current?.focus(); } }} />
                        <TextField fullWidth select  label="Condition" name="Condition" value={formik.values.Condition} onChange={(event) => { const condition = Number(event.target.value) as ReceivingCondition; receivingConditionRef.current = condition; formik.setFieldValue('Condition', condition); }} onBlur={formik.handleBlur} disabled={!canEditQuantity} error={formik.touched.Condition && Boolean(formik.errors.Condition)} helperText={formik.touched.Condition && formik.errors.Condition}>
                            {Object.entries(receivingConditions).map(([id, label]) => <MenuItem key={id} value={Number(id)}>{label}</MenuItem>)}
                        </TextField>
                        <TextField
                            name="Quantity"
                            value={quantityValue}
                            onChange={(event) => { updateQuantityValue(event.target.value === '' ? '' : Number(event.target.value)); updateQuantityScanMode(hasRemainingQuantity); }}
                            fullWidth
                            label="To receive"
                            type="number"
                            inputProps={{ min: 0, step: printProductLabel ? 1 : 'any', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                            error={printProductLabel && quantityValue !== '' && !Number.isSafeInteger(quantityToReceive)}
                            helperText={printProductLabel && quantityValue !== '' && !Number.isSafeInteger(quantityToReceive) ? 'Use a whole number when printing one label per product.' : undefined}
                            disabled={!canEditQuantity}
                            sx={{
                                '& .MuiOutlinedInput-root:not(.Mui-disabled)': {
                                    bgcolor: 'action.selected',
                                    fontWeight: 700,
                                },
                                '& .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2,
                                },
                                '& .MuiInputLabel-root:not(.Mui-disabled)': {
                                    color: 'primary.main',
                                    fontWeight: 600,
                                },
                            }}
                        />
                    </Box>
                </ContentCard>
            )}

            <ContentCard key="receiving-orders" {...{ cardId: 'ReceivingOrders', md: 12, cardTypes: [CardType.noHeader], contentPadding: 0 }}>
                <CustomDataGrid columns={columns} tableSettings={tableSettings} />
            </ContentCard>

        </ReceivingOrdersLayout>
    );
};

const ReceivingStatus = ({ status, hasSelectedRow }: { status: ReceivingStatusValue; hasSelectedRow: boolean }) => {
    const tone = status.kind === 'done' ? 'success' : status.kind === 'surplus' ? 'warning' : 'info';
    const isPending = status.kind === 'pending';
    const label = status.kind === 'done' ? 'Done' : status.kind === 'surplus' ? 'surplus received' : status.kind === 'remaining' ? 'left to receive' : status.kind === 'receiving' ? 'Receiving...' : hasSelectedRow ? 'Awaiting quantities' : 'Select a receiving row';
    const StatusIcon = status.kind === 'done' ? CheckCircleOutlineIcon : status.kind === 'surplus' ? WarningAmberRoundedIcon : status.kind === 'remaining' ? Inventory2OutlinedIcon : InfoOutlinedIcon;

    return (
        <Box component="fieldset" sx={{ m: 0, px: 1, pb: 0.5, pt: 0, minWidth: 0, width: 'fit-content', maxWidth: '100%', border: '1px solid', borderRadius: 1, borderColor: isPending ? 'divider' : `${tone}.main`, bgcolor: (theme) => isPending ? 'transparent' : alpha(theme.palette[tone].main, 0.06) }}>
            <Typography component="legend" sx={{ px: 0.5, fontSize: '0.65rem', lineHeight: 1.2, color: isPending ? 'text.secondary' : `${tone}.main` }}>Receiving status</Typography>
            <Box role="status" aria-live="polite" aria-atomic="true" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 22, color: isPending ? 'text.secondary' : `${tone}.main` }}>
                {status.kind === 'receiving' ? <CircularProgress size={18} color="inherit" aria-label="Receiving products" /> : <StatusIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.5 }}>
                    {status.quantity != null && <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{status.quantity.toLocaleString(undefined, { maximumFractionDigits: 20 })}</Typography>}
                    <Typography component="span" sx={{ fontSize: '0.8rem', lineHeight: 1.3, fontWeight: isPending ? 400 : 600 }}>{label}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

const ReceivingRowPicker = ({ scan, rows, status, onSelect, onClose }: {
    scan: string;
    rows: ReceivingOrderRow[];
    status: ReceivingOrdersTableData['status'];
    onSelect: (row: ReceivingOrderRow) => void;
    onClose: () => void;
}) => {
    const [tableColumns, tableSettings] = useReceivingOrderMatchesTable(rows, onSelect, status);
    const columns = useMemo(() => withEanColumn(tableColumns), [tableColumns]);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl" aria-labelledby="receiving-row-picker-title">
            <DialogTitle id="receiving-row-picker-title">Choose a receiving order row</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>Multiple rows match "{scan}". Check the order and sender, then choose a row.</Typography>
                <CustomDataGrid columns={columns} tableSettings={tableSettings} />
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
        </Dialog>
    );
};


export default ReceivingOrders;

export { default as WarehouseFilter } from 'src/components/atoms/WarehouseSelector';
